import * as React from 'react';
import {
    GoogleAuthProvider,
    User,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    signInWithCredential,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut as firebaseSignOut,
    updateProfile,
} from 'firebase/auth';
import {Capacitor} from '@capacitor/core';
import {FirebaseAuthentication} from '@capacitor-firebase/authentication';
import {auth} from '../firebase/firebase';
import {LoginDialog} from '../components/auth/LoginDialog';
import {AuthFailureStage, AuthMethod, setTelemetryUser, track} from '../utils/telemetry';

export type AuthStatus = 'loading' | 'ready';

interface AuthContextValue {
    user: User | null;
    status: AuthStatus;
    signInWithGoogle: () => Promise<void>;
    signInWithEmail: (email: string, password: string) => Promise<void>;
    registerWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
    signOut: () => Promise<void>;
    getIdToken: () => Promise<string | null>;
    promptLogin: (reason?: string) => void;
    requireAuth: (action: () => void, reason?: string) => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const googleProvider = new GoogleAuthProvider();

// Detect a true user-initiated cancel from Credential Manager / GoogleSignIn so
// we don't reopen another picker after the user explicitly dismissed one. The
// underlying exception class is GetCredentialCancellationException (or a
// classic ApiException with status code 12501 from the legacy picker).
const isUserCancellation = (e: unknown): boolean => {
    const msg = String((e as Error)?.message ?? '');
    return /CancellationException|user canceled|cancelled by the user|sign[- ]?in (was )?cancell?ed|status\s*=\s*12501|\b12501\b/i.test(msg);
};

// Extract a short, PII-free identifier from an auth error that's useful for
// remote diagnosis. Tries (in order):
//   1. Firebase auth error code        e.g. 'auth/invalid-credential'
//   2. Android Credential Manager class e.g. 'GetCredentialUnknownException'
//   3. Google Play Services ApiException status e.g. 'ApiException:10'
//   4. Common literal Credential Manager messages -> their class name (the
//      Capacitor plugin strips the exception class from the message and
//      forwards only getMessage(); we recognize the canonical strings).
//   5. The Error.name property         e.g. 'TypeError'
//   6. Truncated message
// Capped at 80 chars to stay well under GA4's 100-char param limit.
const extractErrorCode = (e: unknown): string => {
    const fbCode = (e as {code?: string})?.code;
    if (fbCode && typeof fbCode === 'string') return fbCode.slice(0, 80);
    const msg = String((e as Error)?.message ?? '');
    const credClass = msg.match(/([A-Z][A-Za-z]*Exception)/)?.[1];
    if (credClass) {
        const apiStatus = msg.match(/ApiException:?\s*(\d+)/)?.[1];
        if (credClass === 'ApiException' && apiStatus) return `ApiException:${apiStatus}`;
        return credClass;
    }
    // Bare ApiException status (no class name in message): "10:", "12501:", etc.
    const bareStatus = msg.match(/^\s*(\d{1,5})\s*:/)?.[1];
    if (bareStatus) return `ApiException:${bareStatus}`;
    // Canonical Credential Manager getMessage() strings -> exception class.
    if (/^No credentials available/i.test(msg)) return 'NoCredentialException';
    if (/^User cancell?ed/i.test(msg)) return 'GetCredentialCancellationException';
    if (/^Activity is cancell?ed/i.test(msg)) return 'GetCredentialCancellationException';
    if (/^The request was interrupted/i.test(msg)) return 'GetCredentialInterruptedException';
    if (/^Get credential provider configuration/i.test(msg)) return 'GetCredentialProviderConfigurationException';
    if (/^Credential Manager unsupport/i.test(msg)) return 'GetCredentialUnsupportedException';
    const name = (e as Error)?.name;
    if (name && name !== 'Error') return name.slice(0, 80);
    return msg ? msg.slice(0, 80) : 'unknown';
};

// Redact the exception message body so we can ship it to telemetry safely.
// Firebase auth messages occasionally include the user's email or display
// name (e.g. "There is no user record corresponding to user@example.com").
// Strip those before logging; cap at 100 chars (GA4 per-param limit).
const redactErrorMessage = (e: unknown): string => {
    let msg = String((e as Error)?.message ?? '');
    if (!msg) return '';
    msg = msg.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>');
    msg = msg.replace(/\s+/g, ' ').trim();
    return msg.slice(0, 100);
};

const reportSignInFailure = (method: AuthMethod, stage: AuthFailureStage, e: unknown): void => {
    void track('auth_sign_in_failed', {
        method,
        stage,
        code: extractErrorCode(e),
        message: redactErrorMessage(e),
        platform: Capacitor.getPlatform(),
    });
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const [user, setUser] = React.useState<User | null>(auth.currentUser);
    const [status, setStatus] = React.useState<AuthStatus>('loading');
    const [loginOpen, setLoginOpen] = React.useState(false);
    const [loginReason, setLoginReason] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        return onAuthStateChanged(auth, nextUser => {
            setUser(nextUser);
            setStatus('ready');
            void setTelemetryUser(nextUser?.uid ?? null);
        });
    }, []);

    const signInWithGoogle = React.useCallback(async () => {
        if (Capacitor.isNativePlatform()) {
            let result;
            try {
                result = await FirebaseAuthentication.signInWithGoogle();
            } catch (e) {
                // Credential Manager throws a broad family of GetCredentialException
                // subclasses — NoCredentialException, GetCredentialUnknownException,
                // GetCredentialProviderConfigurationException, GetCredentialUnsupportedException,
                // GetCredentialInterruptedException — and the message text varies by OEM /
                // Play Services version / managed-Google-account policies. Real-world
                // failure modes we've seen: phones with a work profile (workspace admin
                // refuses to mint ID tokens for third-party apps), older Play Services,
                // accounts without a Google credential registered, custom Android ROMs.
                //
                // Only short-circuit on a real user cancellation; for everything else,
                // fall back to the classic GoogleSignIn picker (Activity-based, no
                // credential-manager dependency) which is broadly compatible.
                const cmCancelled = isUserCancellation(e);
                if (!cmCancelled) reportSignInFailure('google', 'credential_manager', e);
                try {
                    result = await FirebaseAuthentication.signInWithGoogle({useCredentialManager: false});
                } catch (e2) {
                    reportSignInFailure('google', 'legacy_picker', e2);
                    // If the user explicitly cancelled the FIRST stage and then the
                    // second one also returned a cancel-like signal, treat it as a
                    // real user cancellation — silent return is correct UX.
                    if (cmCancelled && isUserCancellation(e2)) return;
                    // If we got here, both stages refused to deliver a credential
                    // without ever showing meaningful UI. Show the user a clear
                    // explanation + alternative — silent failure makes the button
                    // appear broken.
                    if (isUserCancellation(e2)) {
                        const err = new Error('GOOGLE_NO_ELIGIBLE_ACCOUNT');
                        (err as {code?: string}).code = 'GOOGLE_NO_ELIGIBLE_ACCOUNT';
                        throw err;
                    }
                    throw e2;
                }
            }
            const idToken = result.credential?.idToken;
            if (!idToken) {
                const err = new Error('Google sign-in returned no ID token');
                reportSignInFailure('google', 'no_id_token', err);
                throw err;
            }
            const credential = GoogleAuthProvider.credential(idToken);
            try {
                await signInWithCredential(auth, credential);
            } catch (e) {
                reportSignInFailure('google', 'firebase_credential', e);
                throw e;
            }
            void track('auth_sign_in', {method: 'google'});
            return;
        }
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (e) {
            if (isUserCancellation(e)) return;
            reportSignInFailure('google', 'firebase_credential', e);
            throw e;
        }
        void track('auth_sign_in', {method: 'google'});
    }, []);

    const signInWithEmail = React.useCallback(async (email: string, password: string) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (e) {
            reportSignInFailure('email', 'email_password', e);
            throw e;
        }
        void track('auth_sign_in', {method: 'email'});
    }, []);

    const registerWithEmail = React.useCallback(
        async (email: string, password: string, displayName?: string) => {
            try {
                const cred = await createUserWithEmailAndPassword(auth, email, password);
                if (displayName) await updateProfile(cred.user, {displayName});
            } catch (e) {
                reportSignInFailure('register', 'register', e);
                throw e;
            }
            void track('auth_sign_in', {method: 'register'});
        },
        [],
    );

    const signOut = React.useCallback(async () => {
        if (Capacitor.isNativePlatform()) {
            await FirebaseAuthentication.signOut();
        }
        await firebaseSignOut(auth);
    }, []);

    const getIdToken = React.useCallback(async () => {
        return auth.currentUser ? auth.currentUser.getIdToken() : null;
    }, []);

    const promptLogin = React.useCallback((reason?: string) => {
        setLoginReason(reason);
        setLoginOpen(true);
    }, []);

    const requireAuth = React.useCallback(
        (action: () => void, reason?: string) => {
            if (auth.currentUser) {
                action();
                return;
            }
            promptLogin(reason);
        },
        [promptLogin],
    );

    // Close the login dialog once the user is signed in.
    React.useEffect(() => {
        if (user && loginOpen) setLoginOpen(false);
    }, [user, loginOpen]);

    const value: AuthContextValue = {
        user,
        status,
        signInWithGoogle,
        signInWithEmail,
        registerWithEmail,
        signOut,
        getIdToken,
        promptLogin,
        requireAuth,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <LoginDialog
                open={loginOpen}
                reason={loginReason}
                onClose={() => setLoginOpen(false)}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextValue => {
    const ctx = React.useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
    return ctx;
};
